import { useEffect, useRef } from 'react';

/* Hardware barcode scanners act as keyboards: a burst of keystrokes followed
   by Enter (or Tab). This hook listens at the document level so nothing
   needs focus, ignores ordinary typing in inputs, and de-duplicates the
   same code fired twice within 400 ms (camera + wedge, or a double trigger).

   Some scanner drivers commit the whole code as text instead of key events.
   For those, mount <ScanSink/> on the page: an invisible input that keeps
   focus and forwards whatever lands in it.

   With enabled off (a sheet is open) the hook keeps listening and drops the
   code: its Enter would otherwise press whichever button has focus. */

const isEditable = (el) => {
  if (!el) return false;
  if (el.dataset?.scanSink) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

export function useScanInput(onScan, { enabled = true, minLength = 4 } = {}) {
  const cb = useRef(onScan);
  cb.current = onScan;
  const on = useRef(enabled);
  on.current = enabled;
  const state = useRef({ buf: '', last: 0, timer: null, lastCode: '', lastAt: 0 });

  useEffect(() => {
    const s = state.current;

    const emit = (code) => {
      if (!on.current) return;
      const now = Date.now();
      if (code === s.lastCode && now - s.lastAt < 400) return;
      s.lastCode = code;
      s.lastAt = now;
      cb.current?.(code);
    };
    const flush = () => {
      const code = s.buf;
      s.buf = '';
      clearTimeout(s.timer);
      if (code.length >= minLength) emit(code);
    };
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditable(e.target)) return;
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (s.buf) {
          e.preventDefault();
          flush();
        }
        return;
      }
      if (e.key.length !== 1) return;
      const now = Date.now();
      if (now - s.last > 200) s.buf = ''; // a human pause: start over
      s.buf += e.key;
      s.last = now;
      e.preventDefault();
      clearTimeout(s.timer);
      // No Enter within 150 ms of the last char: treat a long burst as a code.
      s.timer = setTimeout(() => {
        if (s.buf.length >= 8) flush();
        else s.buf = '';
      }, 150);
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      clearTimeout(s.timer);
    };
  }, [minLength]);

  return { emit: (code) => cb.current?.(code) };
}

export function ScanSink({ onScan, active = true }) {
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const el = ref.current;
    const focus = () => {
      const a = document.activeElement;
      if (a && a !== el && isEditable(a)) return; // the user is typing somewhere
      el?.focus({ preventScroll: true });
    };
    focus();
    const interval = setInterval(focus, 1500);
    const onPointer = () => setTimeout(focus, 60);
    document.addEventListener('pointerup', onPointer);
    return () => {
      clearInterval(interval);
      document.removeEventListener('pointerup', onPointer);
    };
  }, [active]);

  const handleChange = (e) => {
    const value = e.target.value;
    clearTimeout(timer.current);
    const done = /[\n\r\t]$/.test(value);
    const fire = () => {
      const code = value.replace(/[\n\r\t]/g, '').trim();
      e.target.value = '';
      if (active && code.length >= 4) onScan?.(code);
    };
    if (done) fire();
    else timer.current = setTimeout(fire, 120);
  };

  return (
    <input
      ref={ref}
      data-scan-sink="1"
      aria-hidden="true"
      tabIndex={-1}
      inputMode="none"
      autoComplete="off"
      autoCapitalize="off"
      spellCheck={false}
      onChange={handleChange}
      className="fixed left-0 top-0 h-px w-px opacity-0"
      style={{ pointerEvents: 'none' }}
    />
  );
}
